import { WaitlistAlert } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/notifications.pb';
import dayjs from 'dayjs';
import { dispatchTopBarTraySelectionEvent } from '@frontend/shared';
import { CalendarViewOffices, WorkingHourInfo } from '../components/calendar-view/types';
import { PX_PER_MINUTE } from '../components/calendar-view/utils';
import { DEFAULT_CALENDAR_SCROLL_TO_TIME } from '../constants';

const getOfficeWorkingHours = (calendarData: CalendarViewOffices): WorkingHourInfo[] => {
  for (const office of calendarData) {
    if (office.workingHours?.length) {
      return office.workingHours;
    }
  }
  return [];
};

const getWorkingHoursStart = (workingHours?: WorkingHourInfo[]): string | null => {
  if (!workingHours?.length) return null;

  // Find the earliest start time from all working hours
  const earliestHour = workingHours.reduce((earliest, current) => {
    if (!earliest) return current.startTime;
    return earliest;
  }, workingHours[0]?.startTime);

  return earliestHour || null;
};

const getWorkingHoursEnd = (workingHours?: WorkingHourInfo[]): string => {
  if (!workingHours?.length) return '';

  // Find the latest end time from all working hours
  let latestEndHour: dayjs.Dayjs | null = null;

  for (const workHour of workingHours) {
    const startTime = parseTimeString(workHour.startTime);
    const endTime = startTime.add(workHour.duration, 'minute');

    if (!latestEndHour || endTime.isAfter(latestEndHour)) {
      latestEndHour = endTime;
    }
  }

  return latestEndHour ? latestEndHour.format('HH:mm') : '';
};

const getEarliestAppointmentTime = (calendarData: CalendarViewOffices): string => {
  if (!calendarData?.length) return '';

  let earliestTime = '';

  for (const office of calendarData) {
    if (!office.columnEntities?.length) continue;

    for (const entity of office.columnEntities) {
      if (!entity.events?.length) continue;

      for (const event of entity.events) {
        if (event.appointmentType === 'APPOINTMENT' && event.startHour) {
          if (!earliestTime) {
            earliestTime = event.startHour;
          } else {
            const currentTime = parseTimeString(event.startHour);
            const earliest = parseTimeString(earliestTime);
            if (currentTime.isBefore(earliest)) {
              earliestTime = event.startHour;
            }
          }
        }
      }
    }
  }

  return earliestTime;
};

const parseTimeString = (timeStr: string): dayjs.Dayjs => {
  let time = dayjs(timeStr, 'hh:mm A');
  if (!time.isValid()) time = dayjs(timeStr, 'HH:mm');
  if (!time.isValid()) time = dayjs(timeStr, 'h:mm A');
  return time;
};

const subtractMinutesFromTime = (timeStr: string, minutes: number): string => {
  const time = parseTimeString(timeStr);
  return time.subtract(minutes, 'minute').format('HH:mm');
};

const calculateScrollPositionForTime = (targetTime: string): number => {
  const time = parseTimeString(targetTime);

  if (!time.isValid()) {
    throw new Error(`Invalid time format: ${targetTime}`);
  }

  const targetHour = time.hour();
  const targetMinute = time.minute();
  // Calculate total minutes from start hour
  const totalMinutesFromStart = targetHour * 60 + targetMinute;
  // Convert to pixels (2px per minute based on calendar grid)
  const scrollPosition = totalMinutesFromStart * PX_PER_MINUTE;

  return scrollPosition;
};

const scrollToTime = (containerElement: HTMLElement | null, targetTime: string): void => {
  if (!containerElement) {
    return;
  }

  try {
    const scrollPosition = calculateScrollPositionForTime(targetTime);

    containerElement.scrollTo({
      top: scrollPosition,
      behavior: 'smooth',
    });
  } catch (error) {
    console.error('Failed to scroll to time:', error);
  }
};

export const scrollToPosition = (containerElement: HTMLElement | null, calendarData: CalendarViewOffices): void => {
  if (!containerElement || !calendarData?.length) return;

  try {
    let targetTime = DEFAULT_CALENDAR_SCROLL_TO_TIME;
    const officeTimezone = calendarData[0]?.officeTimezone;
    const currentTimeObj = officeTimezone ? dayjs().tz(officeTimezone) : dayjs().tz();
    const isTodayDateSelected = calendarData[0]?.calendarDateValue === currentTimeObj.format('YYYY-MM-DD');

    if (isTodayDateSelected) {
      const workingHours = getOfficeWorkingHours(calendarData);
      const officeStart = workingHours.length ? getWorkingHoursStart(workingHours) : null;
      const officeEnd = workingHours.length ? getWorkingHoursEnd(workingHours) : null;
      const currentTime = currentTimeObj.format('HH:mm');

      if (officeStart) {
        const currentTimeParsed = parseTimeString(currentTime);
        const officeStartParsed = parseTimeString(officeStart);

        if (currentTimeParsed.isBefore(officeStartParsed)) {
          targetTime = subtractMinutesFromTime(officeStart, 15);
        } else {
          const el = document.getElementById('current-time-progress-line');
          if (el) {
            // Get element's absolute position in the document
            const elRect = el.getBoundingClientRect();
            const containerRect = containerElement.getBoundingClientRect();

            // Calculate element's position relative to the scrollable container
            const elRelativeTop = elRect.top - containerRect.top + containerElement.scrollTop;

            // Calculate center position: element position - half of container height
            const containerHeight = containerElement.clientHeight;
            const centerPosition = elRelativeTop - containerHeight / 2;

            // Check if centering would scroll past office end hours
            if (officeEnd) {
              // Add 30 minutes buffer to office end time
              const officeEndPlusBuffer = parseTimeString(officeEnd).add(30, 'minute').format('HH:mm');
              const officeEndScrollPosition = calculateScrollPositionForTime(officeEndPlusBuffer);
              const maxAllowedScroll = officeEndScrollPosition - containerHeight;
              const finalScrollPosition = Math.min(centerPosition, maxAllowedScroll);

              containerElement.scrollTo({
                top: finalScrollPosition,
                behavior: 'smooth',
              });
            } else {
              containerElement.scrollTo({
                top: centerPosition,
                behavior: 'smooth',
              });
            }
          }
          return;
        }
      }
    }
    // For days other than today
    else {
      const workingHours = getOfficeWorkingHours(calendarData);
      const officeStart = workingHours.length ? getWorkingHoursStart(workingHours) : null;

      if (officeStart) {
        targetTime = subtractMinutesFromTime(officeStart, 15);
      } else {
        const earliestAppt = getEarliestAppointmentTime(calendarData);
        if (earliestAppt) {
          targetTime = subtractMinutesFromTime(earliestAppt, 15);
        }
      }
    }

    // Scroll to the target time
    scrollToTime(containerElement, targetTime);
  } catch (error) {
    console.error('Failed to execute calendar scroll:', error);
  }
};

export const handleWaitlistAppointmentNotificationClick = (_payload: WaitlistAlert) => {
  dispatchTopBarTraySelectionEvent('quickfill');
};
